/**
 * The local player's server-authoritative combat state (plan 03 phase 2).
 *
 * `Player.inCombat` is mirrored by the server's passive-regen tick from the
 * same gate that blocks HP regen, so "in combat" on the frame and "no regen"
 * on the server can never disagree. Delivered like `godMode`: the local
 * player's schema rides SERVER_STATE_UPDATE on every own-player onChange.
 */

import { useEffect, useRef, useState } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';

export function useServerInCombat(): boolean {
  const [inCombat, setInCombat] = useState(false);
  // Set state only on a flip: an equal-value setState on every own-player
  // patch can still render the frame once before React bails out (lane AH).
  const current = useRef(inCombat);
  useEffect(() => EventBus.getInstance().on(
    EVENTS.SERVER_STATE_UPDATE,
    ({ playerSchema }: { playerSchema?: { inCombat?: boolean } }) => {
      if (!playerSchema) return;
      const next = Boolean(playerSchema.inCombat);
      if (next === current.current) return;
      current.current = next;
      setInCombat(next);
    },
  ), []);
  return inCombat;
}

/** Whether the HP bar should show out-of-combat regen right now. */
export function isRegeneratingHealth(
  player: { health: number; maxHealth: number; isDead?: boolean },
  inCombat: boolean,
  outOfCombatPctPerSecond: number,
): boolean {
  return !inCombat && !player.isDead && outOfCombatPctPerSecond > 0
    && player.health > 0 && player.health < player.maxHealth;
}
